import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect } from "react";
import "@/App.css";

// Layout components
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";

// Page components
import HomePage from "./pages/HomePage";
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import ProductPage from "./pages/ProductPage";
import ApplicationPage from "./pages/ApplicationPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderConfirmationPage from "./pages/OrderConfirmationPage";
import PolicyPage from "./pages/PolicyPage";
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminOrdersPage from "./pages/admin/AdminOrdersPage";
import AdminReadinessPage from "./pages/admin/AdminReadinessPage";
import AuthCallback from "./pages/admin/AuthCallback";
import { CartProvider } from "./context/CartContext";
import { CommerceProvider } from "./context/CommerceContext";
import { CartDrawer } from "./components/cart/Cart";
import NotFoundPage from "./pages/NotFoundPage";
import { applyAnalyticsPreference } from "./components/AnalyticsOptOut";

// Honor stored analytics opt-out / Global Privacy Control before anything else renders
const AnalyticsPreference = () => {
  useEffect(() => { applyAnalyticsPreference(); }, []);
  return null;
};

// Scroll to top on route change, but handle hash links
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // If there's a hash, scroll to that element
    if (hash) {
      const element = document.getElementById(hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      // Otherwise scroll to top
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
};

const AppRoutes = () => {
  const location = useLocation();
  // Emergent Google auth returns to {origin}/admin/orders#session_id=... — exchange it before any protected route runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      {/* Homepage */}
      <Route path="/" element={<HomePage />} />

      {/* Blog routes */}
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />

      {/* Product routes: canonical Buddy™/Hank™ pages; legacy slugs redirect */}
      <Route path="/products/buddy" element={<ApplicationPage />} />
      <Route path="/products/hank" element={<ApplicationPage />} />
      <Route path="/products/:slug" element={<ProductPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/order/confirmation" element={<OrderConfirmationPage />} />
      <Route path="/policies/:slug" element={<PolicyPage />} />

      {/* Internal admin (Google sign-in + allowlist; server-enforced) */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/orders" element={<AdminOrdersPage />} />
      <Route path="/admin/readiness" element={<AdminReadinessPage />} />
      <Route path="/admin" element={<AdminLoginPage />} />

      {/* 404 - Page Not Found */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <CommerceProvider>
        <CartProvider>
        <ScrollToTop />
        <AnalyticsPreference />
        <CartDrawer />
        <div className="App bg-[#0A0A0A] min-h-screen">
          <Header />
          <AppRoutes />
          <Footer />
        </div>
        </CartProvider>
        </CommerceProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
